import { useState } from 'react';
import {
  Button,
  H1,
  H2,
  Input,
  Paragraph,
  Spinner,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { Habit } from '@mrrmlab/shared-types';
import { useCreateHabit, useDeleteHabit, useHabits } from '../hooks';

function todayIsoDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function formatNextDue(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function HabitsSettingsScreen() {
  const list = useHabits();
  const create = useCreateHabit();
  const del = useDeleteHabit();

  const [title, setTitle] = useState('');
  const [recurrenceDays, setRecurrenceDays] = useState('7');
  const [anchorDate, setAnchorDate] = useState(todayIsoDate());

  const recurrenceNum = Number.parseInt(recurrenceDays, 10);
  const canSubmit =
    title.trim().length > 0 &&
    Number.isInteger(recurrenceNum) &&
    recurrenceNum >= 1 &&
    recurrenceNum <= 365 &&
    /^\d{4}-\d{2}-\d{2}$/.test(anchorDate);

  const onAdd = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        title: title.trim(),
        recurrenceDays: recurrenceNum,
        anchorDate,
      },
      {
        onSuccess: () => {
          setTitle('');
          setRecurrenceDays('7');
          setAnchorDate(todayIsoDate());
        },
      },
    );
  };

  return (
    <Screen scrollable>
      <YStack gap="$2">
        <H1>Routinen</H1>
        <Text fontSize="$3" opacity={0.7}>
          Wiederkehrende Aufgaben (z.B. wöchentlicher Einkauf). Pro Fälligkeit wird
          automatisch ein Tagesplan-Eintrag erzeugt.
        </Text>
      </YStack>

      <YStack
        gap="$3"
        padding="$4"
        borderRadius="$4"
        borderWidth={1}
        borderColor="$borderColor"
        backgroundColor="$color2"
      >
        <H2 fontSize="$6">Routine anlegen</H2>
        <YStack gap="$2">
          <Text fontSize="$2">Titel</Text>
          <Input
            placeholder="z.B. Wöchentlicher Einkauf"
            value={title}
            onChangeText={setTitle}
            accessibilityLabel="Routine-Titel"
          />
        </YStack>
        <YStack gap="$2">
          <Text fontSize="$2">Wiederholung in Tagen</Text>
          <XStack gap="$2">
            {([{ label: 'Täglich', days: '1' }, { label: 'Wöchentlich', days: '7' }, { label: 'Monatlich', days: '30' }] as const).map(({ label, days }) => (
              <Button
                key={days}
                size="$2"
                theme={recurrenceDays === days ? 'active' : undefined}
                onPress={() => setRecurrenceDays(days)}
              >
                {label}
              </Button>
            ))}
          </XStack>
          <Input
            placeholder="7"
            value={recurrenceDays}
            onChangeText={setRecurrenceDays}
            keyboardType="number-pad"
            accessibilityLabel="Routine-Wiederholung in Tagen"
          />
          {recurrenceDays.length > 0 && (Number.isNaN(Number.parseInt(recurrenceDays, 10)) || Number.parseInt(recurrenceDays, 10) < 1 || Number.parseInt(recurrenceDays, 10) > 365) ? (
            <Text fontSize="$2" color="$red10">
              Bitte eine Zahl zwischen 1 und 365 eingeben.
            </Text>
          ) : null}
        </YStack>
        <YStack gap="$2">
          <Text fontSize="$2">Erstes Fälligkeits-Datum</Text>
          <Input
            placeholder="JJJJ-MM-TT"
            value={anchorDate}
            onChangeText={setAnchorDate}
            autoCapitalize="none"
            autoCorrect={false}
            accessibilityLabel="Routine-Anker-Datum"
          />
          {anchorDate.length > 0 && !/^\d{4}-\d{2}-\d{2}$/.test(anchorDate) ? (
            <Text fontSize="$2" color="$red10">
              Format: JJJJ-MM-TT (z.B. {todayIsoDate()})
            </Text>
          ) : null}
        </YStack>
        {create.isError ? (
          <Text fontSize="$2" color="$red10">
            {create.error.message}
          </Text>
        ) : null}
        <Button
          theme="active"
          onPress={onAdd}
          disabled={create.isPending || !canSubmit}
          accessibilityLabel="Routine hinzufügen"
        >
          {create.isPending ? 'Speichere…' : 'Hinzufügen'}
        </Button>
      </YStack>

      <YStack gap="$2">
        <H2 fontSize="$6">Aktive Routinen</H2>
        {list.isPending ? (
          <Spinner />
        ) : list.isError ? (
          <Paragraph color="$red10">
            Konnte Routinen nicht laden: {list.error.message}
          </Paragraph>
        ) : (list.data ?? []).length === 0 ? (
          <Paragraph opacity={0.7}>Noch keine Routine angelegt.</Paragraph>
        ) : (
          <YStack gap="$2">
            {(list.data ?? []).map((habit) => (
              <HabitRow
                key={habit.id}
                habit={habit}
                onDelete={() => del.mutate(habit.id)}
                deleting={del.isPending && del.variables === habit.id}
              />
            ))}
          </YStack>
        )}
      </YStack>
    </Screen>
  );
}

function HabitRow({
  habit,
  onDelete,
  deleting,
}: {
  habit: Habit;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
    >
      <YStack gap="$1">
        <Text fontSize="$5" fontWeight="600">{habit.title}</Text>
        <Text fontSize="$2" opacity={0.6}>
          alle {habit.recurrenceDays} Tage · nächste Fälligkeit {formatNextDue(habit.nextDueAt)}
        </Text>
      </YStack>
      <XStack gap="$2">
        <Button
          size="$3"
          chromeless
          color="$red10"
          onPress={onDelete}
          disabled={deleting}
          accessibilityLabel={`Entfernen: ${habit.title}`}
        >
          {deleting ? 'Lösche…' : 'Entfernen'}
        </Button>
      </XStack>
    </YStack>
  );
}
